import React, { ReactNode, PropsWithChildren } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import i18n from 'utils/i18n';
import { iconNames } from './icon';
import { isExternalLink } from 'utils/helpers';

export type ContentAlarmProps = PropsWithChildren<{
  type: 'warning' | 'tip' | 'caution' | 'important' | 'note';
  indent?: boolean;
}>;

function transformLinks(node: ReactNode): ReactNode {
  if (!React.isValidElement(node)) return node;

  if (node.type === 'a') {
    type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;
    const anchor = node as React.ReactElement<AnchorProps>;

    const { href = '', children, title } = anchor.props;

    if (isExternalLink(href)) {
      return React.cloneElement<AnchorProps>(anchor, {
        rel: 'noopener noreferrer',
        target: '_blank',
        children: (
          <>
            {children}
            <span className="docon docon-navigate-external" />
          </>
        ),
      });
    } else {
      return (
        <Link href={href} title={title || ''}>
          {children}
        </Link>
      );
    }
  }

  if (node.props?.children) {
    const newChildren = React.Children.map(node.props.children, transformLinks);
    return React.cloneElement(node, {}, newChildren);
  }

  return node;
}

export const ContentAlarm: React.FC<ContentAlarmProps> = ({ type, children, indent = false }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[locale as AcceptedLocales];

  const iconsClasses: Record<string, iconNames> = {
    warning: 'warning',
    tip: 'tip',
    caution: 'caution',
    important: 'info',
    note: 'warning-2',
  };

  const transformedChildren = React.Children.map(children, transformLinks);

  return (
    <StyledContentAlarm className={`alarm alarm-${type}`} indent={indent}>
      <p className="alert-title">
        <i className={`icon icon-${iconsClasses[type]}`} />
        <span>{dictionary[type]}</span>
      </p>

      <div className="alarm-content">{transformedChildren}</div>
    </StyledContentAlarm>
  );
};

export const StyledContentAlarm = styled.div<{ indent: boolean }>`
  display: block;
  position: relative;
  border-radius: 6px;
  font-size: 0.875rem;
  padding: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  background-color: var(--body-background-dark);
  color: var(--text);
  transition: height 0.5s ease-in, opacity 0.5s ease-in;
  word-wrap: break-word;
  word-break: break-word;
  border: var(--border-white-high-contrast);
  margin-left: ${({ indent }) => (indent ? '38px' : '0')};

  > :first-child {
    margin-top: 0;
  }

  .alert-title {
    font-weight: 600;
    display: flex;
    align-items: center;
    line-height: 100%;
    i {
      margin-right: 6px;
    }
  }

  &.alarm-note {
    background-color: var(--info-background);
    border: var(--info-background-glow-high-contrast);
    .alert-title {
      color: var(--info-dark);
    }
  }
  &.alarm-tip {
    background-color: var(--success-background);
    border: var(--success-background-glow-high-contrast);
    .alert-title {
      color: var(--success-dark);
    }
  }
  &.alarm-warning {
    background-color: var(--warning-background);
    border: var(--warning-background-glow-high-contrast);
    .alert-title {
      color: var(--warning-dark);
    }
  }
  &.alarm-caution {
    background-color: var(--danger-background);
    border: var(--danger-background-glow-high-contrast);
    .alert-title {
      color: var(--danger-dark);
    }
  }
  &.alarm-important {
    background-color: var(--primary-background);
    border: var(--primary-background-glow-high-contrast);
    .alert-title {
      color: var(--primary-dark);
    }
  }
`;

export default ContentAlarm;
